import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { usuariosService, perfisCadastroService, type FiltrosUsuarios } from '../services/usuariosService';
import type { CriarUsuarioRequest, AtualizarUsuarioRequest, ResetSenhaRequest, SalvarPerfilRequest } from '../types';

const KEYS = {
  usuarios: ['usuarios'] as const,
  perfis:   ['perfis-crud'] as const,
};

/** Lista da tela Usuários: filtros, ordenação e paginação no servidor. */
export function useUsuariosPesquisa(filtros: FiltrosUsuarios) {
  return useQuery({
    queryKey: [...KEYS.usuarios, 'pesquisa', filtros],
    queryFn:  () => usuariosService.pesquisar(filtros),
    placeholderData: keepPreviousData,
  });
}

export function useUsuarios(busca?: string) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [...KEYS.usuarios, busca],
    queryFn:  () => usuariosService.listar(busca),
  });

  const criar = useMutation({
    mutationFn: (req: CriarUsuarioRequest) => usuariosService.criar(req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.usuarios }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: AtualizarUsuarioRequest }) =>
      usuariosService.atualizar(id, req),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.usuarios }),
  });

  const resetSenha = useMutation({
    mutationFn: ({ id, req }: { id: number; req: ResetSenhaRequest }) =>
      usuariosService.resetSenha(id, req),
  });

  const inativar = useMutation({
    mutationFn: (id: number) => usuariosService.inativar(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: KEYS.usuarios }),
  });

  return { query, criar, atualizar, resetSenha, inativar };
}

export function usePerfisAdminCrud() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.perfis,
    queryFn:  () => perfisCadastroService.listar(),
  });

  const criar = useMutation({
    mutationFn: (req: SalvarPerfilRequest) => perfisCadastroService.criar(req),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: KEYS.perfis });
      qc.invalidateQueries({ queryKey: ['perfis-lista'] }); // invalidate combo too
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarPerfilRequest }) =>
      perfisCadastroService.atualizar(id, req),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: KEYS.perfis });
      qc.invalidateQueries({ queryKey: ['perfis-lista'] });
    },
  });

  const excluir = useMutation({
    mutationFn: (id: number) => perfisCadastroService.excluir(id),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: KEYS.perfis });
      qc.invalidateQueries({ queryKey: ['perfis-lista'] });
    },
  });

  return { query, criar, atualizar, excluir };
}
